import { AppError } from '@packages/shared/domain/shared/AppError';
import type { StatusCodes } from 'http-status-codes';

import { type QueryParams, QueryStringBuilder } from './QueryStringBuilder';

import config from '@/config/config';

const API_BASE_URL = config.BACKEND_URL;
const API_URL = `${API_BASE_URL}/api`;

interface RequestOptions {
	auth?: boolean;
	headers?: HeadersInit;
	params?: QueryParams;
}

export class HttpClient {
	static get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
		return HttpClient.request<T>(endpoint, { ...options, method: 'GET' });
	}

	static post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
		return HttpClient.request<T>(endpoint, {
			...options,
			method: 'POST',
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	static put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
		return HttpClient.request<T>(endpoint, {
			...options,
			method: 'PUT',
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	static patch<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
		return HttpClient.request<T>(endpoint, {
			...options,
			method: 'PATCH',
			body: data ? JSON.stringify(data) : undefined,
		});
	}

	static delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
		return HttpClient.request<T>(endpoint, { ...options, method: 'DELETE' });
	}

	static async stream(endpoint: string, data?: unknown, options?: RequestOptions & { signal?: AbortSignal }): Promise<Response> {
		const { auth = false, headers, params, signal } = options ?? {};

		const url = HttpClient.buildUrl(endpoint, params);

		const defaultHeaders: Record<string, string> = {};
		if (data) {
			defaultHeaders['Content-Type'] = 'application/json';
		}

		const requestConfig: RequestInit = {
			method: 'POST',
			headers: { ...defaultHeaders, ...headers },
			body: data ? JSON.stringify(data) : undefined,
			signal,
		};

		if (auth) {
			requestConfig.credentials = 'include';
		}

		const response = await fetch(url, requestConfig);

		if (!response.ok) {
			throw await HttpClient.buildError(response);
		}

		return response;
	}

	private static async request<T>(endpoint: string, config: RequestInit & RequestOptions = {}): Promise<T> {
		const { auth = false, headers, params, ...fetchOptions } = config;

		const url = HttpClient.buildUrl(endpoint, params);

		const method = fetchOptions.method ?? 'GET';
		const isMutatingMethod = method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';
		const shouldSendEmptyBody = isMutatingMethod && fetchOptions.body == null;

		const defaultHeaders: Record<string, string> = {};
		if (fetchOptions.body || shouldSendEmptyBody) {
			defaultHeaders['Content-Type'] = 'application/json';
		}

		const requestConfig: RequestInit = {
			...fetchOptions,
			headers: { ...defaultHeaders, ...headers },
			body: shouldSendEmptyBody ? '{}' : fetchOptions.body,
		};

		if (auth) {
			requestConfig.credentials = 'include';
		}

		const response = await fetch(url, requestConfig);

		if (!response.ok) {
			throw await HttpClient.buildError(response);
		}

		return HttpClient.parseResponseBody<T>(response);
	}

	private static buildUrl(endpoint: string, params?: QueryParams): string {
		const queryString = params ? QueryStringBuilder.build(params) : '';
		const suffix = queryString ? `?${queryString}` : '';
		return `${API_URL}${endpoint}${suffix}`;
	}

	private static async buildError(response: Response): Promise<AppError> {
		const errorData = await response.json().catch(() => ({
			message: 'An error occurred',
			description: `HTTP ${response.status}`,
		}));

		return new AppError({
			message: errorData.message || 'Request failed',
			httpCode: response.status as StatusCodes,
			description: errorData.description,
			name: 'ApiError',
			data: errorData,
		});
	}

	private static async parseResponseBody<T>(response: Response): Promise<T> {
		if (response.status === 204) {
			return undefined as T;
		}

		const text = await response.text();
		if (!text) {
			return undefined as T;
		}

		return JSON.parse(text);
	}
}
